import { useRef, useState, type FC } from 'react'

import { Address, LoaderWrapper } from '@skytech/ui'
import { useMutation, useQuery } from '@tanstack/react-query'
import type { FormikProps } from 'formik'
import { Form, Formik } from 'formik'
import AccountTieIcon from 'mdi-react/AccountTieIcon'
import CardAccountDetailsOutlineIcon from 'mdi-react/CardAccountDetailsOutlineIcon'
import MapMarkerOutlineIcon from 'mdi-react/MapMarkerOutlineIcon'

import { queryClient } from '@/app/queryClient'
import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'
import { NoResults } from '@/components/NoResults'
import Tabs from '@/components/Tabs'
import { useIsThreeStepIT } from '@/hooks/access'
import type { LeasingSettings } from '@/resources/leasing'
import { leasingResources } from '@/resources/leasing'
import type { UpdateEndOfLeasePayload } from '@/resources/leasing/types'
import { commonQueries } from '@/resources/queries'

import { EndOfLeaseForm } from './EndOfLeaseForm'
import {
  DEFAULT_ASSET_RETURN_OFFSET,
  DEFAULT_EMPLOYEE_SELECTION_OPEN_OFFSET,
  DEFAULT_EMPLOYEE_START_OFFSET,
  mapInitialDefaultReminders,
  mapInitialReminders,
  mapRemindersToUpdate,
  showSpecificFields,
  type GroupedReminders,
} from './helpers'
import { reminderMaxValue } from './Timeline/Reminders'

export type LeasingSettingsFormFields = {
  endOfLeaseIsActive: boolean
  sendEndOfLeaseNotification?: boolean | null
  notificationEmails?: string[] | null
  employeeStartOffset?: number | null
  employeeSelectionOpenOffset?: number | null
  assetReturnOffset?: number | null
  reminders: GroupedReminders[]
}

type LeasingSettingsFormFieldsErrors = {
  endOfLeaseIsActive?: string
  notificationEmails?: string
  employeeStartOffset?: string
  employeeSelectionOpenOffset?: string
  assetReturnOffset?: string
  reminders?: Array<{ offsetDays?: string; types?: string }>
}

export const LeaseSettings: FC<{ policyId: number; onClose: () => void; isDisabled?: boolean }> = ({
  policyId,
  onClose,
  isDisabled = false,
}) => {
  const translate = useTranslation()
  const isThreeStepIT = useIsThreeStepIT()
  const formRef = useRef<FormikProps<LeasingSettingsFormFields> | null>(null)
  const [selectedTab, setSelectedTab] = useState('end-of-lease')
  const [errorMsg, setErrorMsg] = useState('')
  const tabs = [
    { value: 'end-of-lease', label: <Translation id="End of lease" /> },
    { value: 'vendor-info', label: <Translation id="Vendor info" /> },
  ]
  const { data, isLoading } = useQuery(commonQueries.leasingSettingsByPolicyId(policyId), () =>
    leasingResources.getLeasingSettings(policyId)
  )

  const updateEndOfLeaseSettingsMutation = useMutation(leasingResources.updateEndOfLeaseSettings, {
    onSettled() {
      queryClient.invalidateQueries(commonQueries.leasingSettingsByPolicyId(policyId))
      formRef.current?.resetForm()
    },
    onSuccess() {
      // Clear state after 5 seconds
      setTimeout(() => {
        updateEndOfLeaseSettingsMutation.reset()
      }, 5000)
    },
    onError(error: { json: { message: string } }) {
      setErrorMsg(error.json.message)

      // Clear state after 5 seconds
      setTimeout(() => {
        updateEndOfLeaseSettingsMutation.reset()
      }, 5000)
    },
  })

  const validateReminders = (
    errors: Array<{ offsetDays?: string; types?: string }> = [],
    reminders: GroupedReminders[],
    employeeSelectionOpenOffset?: number | null
  ) => {
    reminders.forEach((reminder, idx) => {
      const currentError: { offsetDays?: string; types?: string } = {}
      const isFirst = idx === 0
      const prev = idx > 0 ? reminders[idx - 1] : null
      const next = idx < reminders.length - 1 ? reminders[idx + 1] : null
      const isActive = reminder.offsetDays !== null && reminder.offsetDays !== ''

      if (!isActive) {
        if (isFirst) {
          currentError.offsetDays = translate('This field is required')
        } else if (
          next &&
          next.offsetDays !== null &&
          next.offsetDays !== '' &&
          Number(next.offsetDays) >= 1
        ) {
          currentError.offsetDays = translate(
            'This reminder should be sent after the previous reminder, meaning fewer than {prevDays} days before end of lease.',
            {
              prevDays: String(prev?.offsetDays || 1),
            }
          )
        }
        errors.push(currentError)
        return
      }

      const isEmailOrSmsSelected = reminder.types.some(t => t.value)
      if (!isEmailOrSmsSelected) {
        currentError.types = translate('Select at least one method (email or sms)')
      }

      if (isActive && Number(reminder.offsetDays) < 1) {
        currentError.offsetDays = translate('Must be at least 1')
      }

      const firstReminderMaxValue = isThreeStepIT
        ? reminderMaxValue
        : employeeSelectionOpenOffset || reminderMaxValue
      if (isFirst && Number(reminder.offsetDays) > firstReminderMaxValue) {
        currentError.offsetDays = translate(
          'This reminder must be sent after shop has opened, meaning fewer than {maxValue} days before end of lease',
          {
            maxValue: String(firstReminderMaxValue),
          }
        )
      }

      if (prev && prev.offsetDays !== null && prev.offsetDays !== '') {
        if (isActive && reminder.offsetDays! >= prev.offsetDays) {
          currentError.offsetDays = translate(
            'This reminder should be sent after the previous reminder, meaning fewer than {prevDays} days before end of lease.',
            {
              prevDays: String(prev.offsetDays),
            }
          )
        }
        const isOneOfPrevOne = reminders.some((r, i) => i < idx && Number(r.offsetDays) === 1)
        if (isActive && (prev.offsetDays === 1 || isOneOfPrevOne)) {
          currentError.offsetDays = translate(`Previous is 1 - this must to be cleared first`)
        }
      }

      const isOneOfNextOne = reminders.some((r, i) => i > idx && Number(r.offsetDays) === 1)
      if (isActive && Number(reminder.offsetDays) === 1 && isOneOfNextOne) {
        currentError.offsetDays = translate(
          'You can not set this reminder to 1 while later reminders exist. Clear the next ones first.'
        )
      }

      errors.push(currentError)
    })
    return errors
  }

  const validateValues = (values: LeasingSettingsFormFields) => {
    const errors: LeasingSettingsFormFieldsErrors = {}

    if (
      values.sendEndOfLeaseNotification &&
      (!values.notificationEmails || values.notificationEmails?.length === 0)
    ) {
      errors.notificationEmails = translate('This field is required')
    }
    if (!values.endOfLeaseIsActive) {
      return errors
    }

    let reminderErrors: Array<{ offsetDays?: string; types?: string }> = []
    const { showEmployeeSelectionOpen, showAssetReturn } = showSpecificFields(
      isThreeStepIT,
      data?.endOfLease?.lockFieldsLeasingSettings
    )

    if (values.employeeStartOffset && values.employeeStartOffset < 0) {
      errors.employeeStartOffset = translate('Incorrect value')
    }

    if (showEmployeeSelectionOpen) {
      if (
        values.employeeStartOffset &&
        values.employeeSelectionOpenOffset &&
        values.employeeStartOffset <= values.employeeSelectionOpenOffset
      ) {
        errors.employeeStartOffset = translate(
          'Employee heads-up can only be sent before shop opens, meaning more than {daysValue} days before lease end.',
          { daysValue: String(values.employeeSelectionOpenOffset) }
        )
      }
    } else {
      const firstReminder = values.reminders[0]
      if (
        values.employeeStartOffset &&
        firstReminder &&
        values.employeeStartOffset <= Number(firstReminder.offsetDays)
      ) {
        errors.employeeStartOffset = translate(
          'Employee heads-up can only be sent before shop opens, meaning more than {daysValue} days before lease end.',
          { daysValue: String(firstReminder.offsetDays) }
        )
      }
    }

    if (values.employeeStartOffset && values.employeeStartOffset > 90) {
      errors.employeeStartOffset = translate('Max 90 days')
    }

    // Non-3S case validation starts
    if (showEmployeeSelectionOpen && !values.employeeSelectionOpenOffset) {
      errors.employeeSelectionOpenOffset = translate('This field is required')
    }
    if (
      showEmployeeSelectionOpen &&
      values.employeeSelectionOpenOffset &&
      values.employeeSelectionOpenOffset < 14
    ) {
      errors.employeeSelectionOpenOffset = translate('Min 14 days')
    }
    if (
      showEmployeeSelectionOpen &&
      values.employeeSelectionOpenOffset &&
      values.employeeSelectionOpenOffset > 365
    ) {
      errors.employeeSelectionOpenOffset = translate('Max 365 days')
    }

    if (showAssetReturn && !values.assetReturnOffset) {
      errors.assetReturnOffset = translate('This field is required')
    }
    if (showAssetReturn && values.assetReturnOffset) {
      if (values.assetReturnOffset <= 0) {
        errors.assetReturnOffset = translate('Incorrect value')
      } else if (values.assetReturnOffset > 365) {
        errors.assetReturnOffset = translate('Max 365 days')
      } else {
        const activeReminderOffsets = values.reminders
          .filter(r => r.offsetDays !== null && r.offsetDays !== '')
          .map(r => Number(r.offsetDays))

        const isNotLessThanEmployeeStart =
          values.employeeStartOffset != null &&
          values.assetReturnOffset >= values.employeeStartOffset

        const isNotLessThanEmployeeSelectionOpen =
          values.employeeSelectionOpenOffset != null &&
          values.assetReturnOffset >= values.employeeSelectionOpenOffset

        const isNotLessThanLastReminder =
          activeReminderOffsets.length > 0 &&
          values.assetReturnOffset >= Math.min(...activeReminderOffsets)

        if (
          isNotLessThanEmployeeStart ||
          isNotLessThanEmployeeSelectionOpen ||
          isNotLessThanLastReminder
        ) {
          errors.assetReturnOffset = translate(
            'Asset return cut-off may only happen after last reminder, meaning fewer days before end of lease.'
          )
        }
      }
    }
    // Non-3S case validation ends

    reminderErrors = validateReminders(
      reminderErrors,
      values.reminders,
      values.employeeSelectionOpenOffset
    )

    if (reminderErrors.some(e => Object.keys(e).length > 0)) {
      errors.reminders = reminderErrors
    }

    return errors
  }

  const onSubmit = async (values: LeasingSettingsFormFields) => {
    if (isDisabled || !formRef.current?.dirty) return

    if (!values.endOfLeaseIsActive) {
      const bodyData: UpdateEndOfLeasePayload = {
        id: data?.endOfLease?.id ?? null,
        isActive: Boolean(values.endOfLeaseIsActive),
        notificationEmails: values.notificationEmails ? values.notificationEmails.join(', ') : null,
        reminders: [],
      }
      if (
        values.sendEndOfLeaseNotification !== null ||
        values.sendEndOfLeaseNotification !== undefined
      ) {
        bodyData.sendEndOfLeaseNotification = Boolean(values.sendEndOfLeaseNotification)
      }
      updateEndOfLeaseSettingsMutation.mutate({ policyId, body: bodyData })
      return
    }

    const reminders = mapRemindersToUpdate(values.reminders, data?.endOfLease?.reminders || [])

    let bodyData: UpdateEndOfLeasePayload = {
      id: data?.endOfLease?.id ?? null,
      isActive: Boolean(values.endOfLeaseIsActive),
      notificationEmails: values.notificationEmails ? values.notificationEmails.join(', ') : null,
      defaultEmployeeStartOffset: values.employeeStartOffset || null,
      reminders: reminders,
    }
    if (
      values.sendEndOfLeaseNotification !== null ||
      values.sendEndOfLeaseNotification !== undefined
    ) {
      bodyData.sendEndOfLeaseNotification = Boolean(values.sendEndOfLeaseNotification)
    }

    const { showEmployeeSelectionOpen, showAssetReturn } = showSpecificFields(
      isThreeStepIT,
      data?.endOfLease?.lockFieldsLeasingSettings
    )
    if (showEmployeeSelectionOpen) {
      bodyData.defaultEmployeeSelectionOpenOffset = values.employeeSelectionOpenOffset || null
    }
    if (showAssetReturn) {
      bodyData.defaultAssetReturnOffset = values.assetReturnOffset || null
    }
    updateEndOfLeaseSettingsMutation.mutate({ policyId, body: bodyData })
  }

  return (
    <>
      <h2>
        <Translation id="Lease settings" />
      </h2>
      <LoaderWrapper isLoading={isLoading}>
        <Formik<LeasingSettingsFormFields>
          initialValues={getInitialValues(data)}
          enableReinitialize
          innerRef={formRef}
          validate={validateValues}
          onSubmit={onSubmit}
        >
          {() => (
            <Form>
              <Tabs
                selectedValue={selectedTab}
                tabs={tabs}
                onClick={tabId => {
                  setSelectedTab(String(tabId))
                }}
              />
              {selectedTab === 'vendor-info' && (
                <>{data ? <GeneralTab {...data} /> : <NoResults />}</>
              )}
              {selectedTab === 'end-of-lease' && (
                <EndOfLeaseForm
                  onPopupClose={onClose}
                  isLoading={updateEndOfLeaseSettingsMutation.isLoading}
                  isError={updateEndOfLeaseSettingsMutation.isError}
                  errorMsg={errorMsg}
                  isSuccess={updateEndOfLeaseSettingsMutation.isSuccess}
                  lockFields={data?.endOfLease?.lockFieldsLeasingSettings}
                  isDisabled={isDisabled}
                  data={data}
                />
              )}
            </Form>
          )}
        </Formik>
      </LoaderWrapper>
    </>
  )
}

const GeneralTab: FC<Omit<LeasingSettings, 'endOfLease'>> = ({
  vendorName,
  orgNumber,
  address1,
  address2,
  zip,
  city,
}) => (
  <div className="flex flex-col gap-5">
    <div className="flex items-start gap-2 sm:gap-4">
      <AccountTieIcon className="h-7 w-7 sm:h-9 sm:w-9" />
      <div>
        <h4 className="m-0 mb-1 whitespace-nowrap text-sm font-normal text-gray-500">
          <Translation id="Leasing vendor" />
        </h4>
        <div className="text-lg leading-none text-gray-700">{vendorName}</div>
      </div>
    </div>

    <div className="flex items-start gap-2 sm:gap-4">
      <CardAccountDetailsOutlineIcon className="h-7 w-7 sm:h-9 sm:w-9" />
      <div>
        <h4 className="m-0 mb-1 whitespace-nowrap text-sm font-normal text-gray-500">
          <Translation id="Org. number" />
        </h4>
        <div className="text-lg leading-none text-gray-700">{orgNumber}</div>
      </div>
    </div>

    <div className="flex items-start gap-2 sm:gap-4">
      <MapMarkerOutlineIcon className="h-7 w-7 sm:h-9 sm:w-9" />
      <div>
        <h4 className="m-0 mb-1 whitespace-nowrap text-sm font-normal text-gray-500">
          <Translation id="Leasing vendor address" />
        </h4>
        <div className="text-lg text-gray-700">
          {address1 || address2 || zip || city ? (
            <Address street1={address1} street2={address2} zip={zip} city={city} />
          ) : (
            <>—</>
          )}
        </div>
      </div>
    </div>
  </div>
)

function getInitialValues(data?: LeasingSettings) {
  const isDefaultValues = Boolean(
    !data?.endOfLease?.defaultEmployeeStartOffset &&
      !data?.endOfLease?.defaultEmployeeSelectionOpenOffset &&
      !data?.endOfLease?.defaultAssetReturnOffset
  )

  const filledReminders = data?.endOfLease?.reminders
    ? mapInitialReminders(data?.endOfLease?.reminders)
    : mapInitialDefaultReminders()

  return {
    endOfLeaseIsActive: data?.endOfLease?.isActive || false,
    sendEndOfLeaseNotification:
      data?.endOfLease?.sendEndOfLeaseNotification === null
        ? null
        : Boolean(data?.endOfLease?.sendEndOfLeaseNotification),

    notificationEmails: data?.endOfLease?.notificationEmails
      ? data.endOfLease.notificationEmails.split(', ')
      : [],
    employeeStartOffset: isDefaultValues
      ? DEFAULT_EMPLOYEE_START_OFFSET
      : data?.endOfLease?.defaultEmployeeStartOffset,
    employeeSelectionOpenOffset: isDefaultValues
      ? DEFAULT_EMPLOYEE_SELECTION_OPEN_OFFSET
      : data?.endOfLease?.defaultEmployeeSelectionOpenOffset,
    reminders: filledReminders,
    assetReturnOffset: isDefaultValues
      ? DEFAULT_ASSET_RETURN_OFFSET
      : data?.endOfLease?.defaultAssetReturnOffset,
  }
}
